import { useMutation, useQuery } from '@tanstack/react-query';
import { api, apiGet } from '../lib/api.js';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'none';

export type BillingSummary = {
  configured: boolean;
  status: SubscriptionStatus;
  plan: 'monthly' | 'annual' | 'none';
  isComped: boolean;
  currentPeriodEnd: string | null;
  seatCount: number;
  access: 'full' | 'locked';
};

/**
 * The tenant's billing summary. `enabled` lets callers skip the fetch for users
 * who shouldn't hit `/billing` (it 400s for a super-admin with no tenant);
 * defaults true so existing call sites are unaffected.
 */
export function useBillingStatus(enabled = true) {
  return useQuery({
    queryKey: ['billing'],
    queryFn: () => apiGet<BillingSummary>('/billing'),
    staleTime: 30_000,
    enabled,
  });
}

/** Start hosted Checkout for a plan; redirects the browser to Stripe on success. */
export function useStartCheckout() {
  return useMutation({
    mutationFn: (plan: 'monthly' | 'annual') =>
      api<{ url: string }>('/billing/checkout', { method: 'POST', body: { plan } }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/** Open the Stripe Customer Portal; redirects the browser on success. */
export function useOpenPortal() {
  return useMutation({
    mutationFn: () => api<{ url: string }>('/billing/portal', { method: 'POST' }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}
